import { createSignal, createMemo, type Component, For } from "solid-js";
import { PanelLayout, type SubTab, type PanelStatus } from "@/shared/panel-base";
import { DataTable, type Column } from "@/shared/data-table";
import { createToolQuery } from "@/shared/query";
import { useToolClient } from "@/shared/tool-client";
import { useConfirm } from "@/shared/confirm-dialog";
import { useWs } from "@/shared/ws";
import { registerPanel } from "@/panel-registry";
import { fuzzyFilter } from "./lib/fuzzy";

interface DhcpScope {
  ScopeId?: string;
  Name?: string;
  StartRange?: string;
  EndRange?: string;
  SubnetMask?: string;
  State?: string;
  Description?: string;
}

interface DhcpLease {
  IPAddress?: string;
  ClientId?: string;
  HostName?: string;
  LeaseExpiryTime?: string;
  AddressState?: string;
}

interface DhcpReservation {
  IPAddress?: string;
  ScopeId?: string;
  ClientId?: string;
  Name?: string;
  Description?: string;
}

interface ListResult<T> {
  rows: T[];
}

const IPV4_RE = /^(?:(?:25[0-5]|2[0-4]\d|[01]?\d?\d)\.){3}(?:25[0-5]|2[0-4]\d|[01]?\d?\d)$/;
const MAC_RE = /^[0-9A-Fa-f]{12}$|^([0-9A-Fa-f]{2}[-:]){5}[0-9A-Fa-f]{2}$/;

function formatCell(value: unknown): string {
  if (value === null || value === undefined) return "";
  if (typeof value === "boolean") return value ? "Oui" : "Non";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}T/.test(value)) {
    try {
      return new Date(value).toLocaleString();
    } catch {
      return value;
    }
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }
  return String(value);
}

const DhcpPanel: Component = () => {
  const ws = useWs();
  const confirm = useConfirm();
  const tools = useToolClient();
  const [selectedScope, setSelectedScope] = createSignal("");
  const [actionStatus, setActionStatus] = createSignal<PanelStatus | null>(null);
  const [scopeFilter, setScopeFilter] = createSignal("");
  const [leaseFilter, setLeaseFilter] = createSignal("");
  const [reservationFilter, setReservationFilter] = createSignal("");

  const enabled = () => ws.state.nativeAgentOnline && ws.state.supportApproved;

  const scopesQuery = createToolQuery<ListResult<DhcpScope>>(() => ({
    tool: "dhcp_scopes_list",
    params: {},
    enabled: enabled(),
    refetchInterval: 0,
  }));

  const leasesQuery = createToolQuery<ListResult<DhcpLease>>(() => ({
    tool: "dhcp_leases_list",
    params: { scopeId: selectedScope() },
    enabled: enabled() && selectedScope() !== "",
    refetchInterval: 0,
  }));

  const reservationsQuery = createToolQuery<ListResult<DhcpReservation>>(() => ({
    tool: "dhcp_reservations_list",
    params: { scopeId: selectedScope() },
    enabled: enabled() && selectedScope() !== "",
    refetchInterval: 0,
  }));

  async function releaseLease(ipAddress: string): Promise<void> {
    const scope = selectedScope();
    if (!scope || !ipAddress) return;
    const ok = await confirm.confirm({
      title: `Liberer bail ${ipAddress}`,
      body: `Confirmer la liberation du bail DHCP ${ipAddress} (scope ${scope}) ?`,
      level: "danger",
      confirmLabel: "Liberer",
      entityName: ipAddress,
    });
    if (!ok) return;
    setActionStatus({ text: `Liberation ${ipAddress}...`, level: "info" });
    try {
      await tools.call(
        "dhcp_lease_release",
        { scopeId: scope, ipAddress },
        { userConfirmedAt: new Date().toISOString() },
      );
      setActionStatus({ text: `Liberation ${ipAddress} reussie.`, level: "info" });
      await leasesQuery.refetch();
    } catch (err) {
      setActionStatus({
        text: `Erreur: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    }
  }

  async function addReservation(): Promise<void> {
    const scope = selectedScope();
    if (!scope) {
      setActionStatus({ text: "Selectionnez d'abord un scope.", level: "warn" });
      return;
    }
    const ipAddress = window.prompt(`Adresse IP de reservation (scope ${scope}):`, "");
    if (!ipAddress) return;
    if (!IPV4_RE.test(ipAddress.trim())) {
      setActionStatus({ text: "Adresse IPv4 invalide.", level: "warn" });
      return;
    }
    const clientId = window.prompt("Adresse MAC (ex: AA-BB-CC-DD-EE-FF):", "");
    if (!clientId) return;
    if (!MAC_RE.test(clientId.trim())) {
      setActionStatus({ text: "Adresse MAC invalide.", level: "warn" });
      return;
    }
    const name = window.prompt("Nom (optionnel):", "") ?? "";
    const description = window.prompt("Description (optionnelle):", "") ?? "";
    const ok = await confirm.confirm({
      title: "Ajouter reservation DHCP",
      body: `Ajouter reservation ${ipAddress} pour MAC ${clientId} dans le scope ${scope} ?`,
      level: "info",
      confirmLabel: "Ajouter",
    });
    if (!ok) return;
    setActionStatus({ text: "Ajout reservation...", level: "info" });
    try {
      await tools.call(
        "dhcp_reservation_add",
        {
          scopeId: scope,
          ipAddress: ipAddress.trim(),
          clientId: clientId.trim(),
          name: name.trim(),
          description: description.trim(),
        },
        { userConfirmedAt: new Date().toISOString() },
      );
      setActionStatus({ text: "Ajout reservation reussi.", level: "info" });
      await reservationsQuery.refetch();
    } catch (err) {
      setActionStatus({
        text: `Erreur: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    }
  }

  async function removeReservation(ipAddress: string): Promise<void> {
    const scope = selectedScope();
    if (!scope || !ipAddress) return;
    const ok = await confirm.confirm({
      title: `Supprimer reservation ${ipAddress}`,
      body: `Confirmer la suppression de la reservation DHCP ${ipAddress} (scope ${scope}) ?`,
      level: "danger",
      confirmLabel: "Supprimer",
      entityName: ipAddress,
    });
    if (!ok) return;
    setActionStatus({ text: `Suppression ${ipAddress}...`, level: "info" });
    try {
      await tools.call(
        "dhcp_reservation_remove",
        { scopeId: scope, ipAddress },
        { userConfirmedAt: new Date().toISOString() },
      );
      setActionStatus({ text: `Suppression ${ipAddress} reussie.`, level: "info" });
      await reservationsQuery.refetch();
    } catch (err) {
      setActionStatus({
        text: `Erreur: ${err instanceof Error ? err.message : String(err)}`,
        level: "danger",
      });
    }
  }

  const filteredScopes = createMemo(() =>
    fuzzyFilter(scopeFilter(), scopesQuery.data?.rows ?? [], (s) =>
      [s.ScopeId ?? "", s.Name ?? "", s.StartRange ?? "", s.EndRange ?? "", s.State ?? "", s.Description ?? ""].join(
        " ",
      ),
    ),
  );

  const filteredLeases = createMemo(() =>
    fuzzyFilter(leaseFilter(), leasesQuery.data?.rows ?? [], (l) =>
      [l.IPAddress ?? "", l.ClientId ?? "", l.HostName ?? "", l.AddressState ?? ""].join(" "),
    ),
  );

  const filteredReservations = createMemo(() =>
    fuzzyFilter(reservationFilter(), reservationsQuery.data?.rows ?? [], (r) =>
      [r.IPAddress ?? "", r.ScopeId ?? "", r.ClientId ?? "", r.Name ?? "", r.Description ?? ""].join(" "),
    ),
  );

  const scopeColumns: Column<DhcpScope>[] = [
    { accessorKey: "ScopeId", header: "ScopeId" },
    { accessorKey: "Name", header: "Name" },
    { accessorKey: "StartRange", header: "StartRange" },
    { accessorKey: "EndRange", header: "EndRange" },
    { accessorKey: "SubnetMask", header: "SubnetMask" },
    { accessorKey: "State", header: "State" },
    { accessorKey: "Description", header: "Description" },
  ];

  const leaseColumns: Column<DhcpLease>[] = [
    { accessorKey: "IPAddress", header: "IPAddress" },
    { accessorKey: "ClientId", header: "ClientId" },
    { accessorKey: "HostName", header: "HostName" },
    {
      accessorKey: "LeaseExpiryTime",
      header: "LeaseExpiryTime",
      cell: (info) => formatCell(info.getValue()),
    },
    { accessorKey: "AddressState", header: "AddressState" },
  ];

  const reservationColumns: Column<DhcpReservation>[] = [
    { accessorKey: "IPAddress", header: "IPAddress" },
    { accessorKey: "ScopeId", header: "ScopeId" },
    { accessorKey: "ClientId", header: "ClientId" },
    { accessorKey: "Name", header: "Name" },
    { accessorKey: "Description", header: "Description" },
  ];

  const scopeSelector = (
    <>
      <span class="panel-toolbar-label">Scope:</span>
      <select
        class="panel-filter-input"
        value={selectedScope()}
        onChange={(e) => setSelectedScope(e.currentTarget.value)}
      >
        <option value="">-- choisir un scope --</option>
        <For each={scopesQuery.data?.rows ?? []}>
          {(s) => <option value={s.ScopeId ?? ""}>{s.Name ? `${s.ScopeId} — ${s.Name}` : s.ScopeId}</option>}
        </For>
      </select>
    </>
  );

  const subtabs: SubTab[] = [
    {
      id: "scopes",
      label: "Scopes",
      render: () => (
        <>
          <div class="panel-toolbar panel-toolbar--filter">
            <input
              type="text"
              class="panel-filter-input"
              placeholder="Recherche fuzzy (ex: 192 act)..."
              spellcheck={false}
              value={scopeFilter()}
              onInput={(e) => setScopeFilter(e.currentTarget.value)}
            />
            <span class="panel-toolbar-label">
              {filteredScopes().length} / {(scopesQuery.data?.rows ?? []).length}
            </span>
            <button type="button" class="btn btn--xs btn--info" onClick={() => scopesQuery.refetch()}>
              Rafraichir
            </button>
          </div>
          <DataTable<DhcpScope> rows={filteredScopes()} columns={scopeColumns} />
        </>
      ),
    },
    {
      id: "leases",
      label: "Baux",
      render: () => (
        <>
          <div class="panel-toolbar panel-toolbar--filter">
            {scopeSelector}
            <button
              type="button"
              class="btn btn--xs btn--info"
              disabled={!selectedScope()}
              onClick={() => leasesQuery.refetch()}
            >
              Rafraichir
            </button>
            <input
              type="text"
              class="panel-filter-input"
              placeholder="Recherche fuzzy (ex: ip host)..."
              spellcheck={false}
              value={leaseFilter()}
              onInput={(e) => setLeaseFilter(e.currentTarget.value)}
            />
            <span class="panel-toolbar-label">
              {filteredLeases().length} / {(leasesQuery.data?.rows ?? []).length}
            </span>
          </div>
          <DataTable<DhcpLease>
            rows={filteredLeases()}
            columns={leaseColumns}
            rowActions={(r) => (
              <button type="button" class="btn btn--xs btn--danger" onClick={() => releaseLease(r.IPAddress ?? "")}>
                Liberer
              </button>
            )}
          />
        </>
      ),
    },
    {
      id: "reservations",
      label: "Reservations",
      render: () => (
        <>
          <div class="panel-toolbar panel-toolbar--filter">
            {scopeSelector}
            <button
              type="button"
              class="btn btn--xs btn--info"
              disabled={!selectedScope()}
              onClick={() => addReservation()}
            >
              + Ajouter
            </button>
            <button
              type="button"
              class="btn btn--xs btn--info"
              disabled={!selectedScope()}
              onClick={() => reservationsQuery.refetch()}
            >
              Rafraichir
            </button>
            <input
              type="text"
              class="panel-filter-input"
              placeholder="Recherche fuzzy (ex: ip mac)..."
              spellcheck={false}
              value={reservationFilter()}
              onInput={(e) => setReservationFilter(e.currentTarget.value)}
            />
            <span class="panel-toolbar-label">
              {filteredReservations().length} / {(reservationsQuery.data?.rows ?? []).length}
            </span>
          </div>
          <DataTable<DhcpReservation>
            rows={filteredReservations()}
            columns={reservationColumns}
            rowActions={(r) => (
              <button
                type="button"
                class="btn btn--xs btn--danger"
                onClick={() => removeReservation(r.IPAddress ?? "")}
              >
                Supprimer
              </button>
            )}
          />
        </>
      ),
    },
  ];

  const status = (): PanelStatus | undefined => {
    if (actionStatus()) return actionStatus() ?? undefined;
    if (!ws.state.nativeAgentOnline) return { text: "Module natif requis.", level: "warn" };
    if (!ws.state.supportApproved) return { text: "Validation client requise.", level: "warn" };
    if (scopesQuery.isFetching || leasesQuery.isFetching || reservationsQuery.isFetching)
      return { text: "Chargement...", level: "info" };
    return undefined;
  };

  return <PanelLayout title="DHCP Server" subtabs={subtabs} status={status()} />;
};

registerPanel("dhcp", DhcpPanel);
export default DhcpPanel;
